import React, {
    useState,
    useContext,
    useCallback,
    useRef,
    useEffect,
} from 'react';
import * as Cookies from 'js-cookie';
import { PageContext } from '../../../contexts/ContextCreator';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import {
    checkHasRequirements,
    registrationFlow,
} from '../../Modals/Flows/EventRegistration/constants';
import { eventRegistrationFlowType } from '../../Modals/Flows/EventRegistration/EventRegistration';
import {
    CONFIRM_SUCCESS_STEP_NAME,
    EVENT_REQUIREMENTS_INPUT_STEP_NAME,
    QUICK_PROFILE_STEP_NAME,
    SELECT_TIME_STEP_NAME,
} from '../../Modals/Flows/Steps/constants';
import {
    EVENT_IN_PROGRESS_BANNER,
    countDown,
    getBannerText,
    getRegistrationText,
    isInThePast,
} from './components/helpers';

const { registrationFlowAPI } = registrationFlow;

const EventRegistrationContext = React.createContext(undefined);

const EventRegistrationProvider = ({
    children,
    event,
    attendeeCount,
    upcomingEvents,
}) => {
    const [userRegistered, setUserRegistered] = useState(event?.userRegistered);
    const [playVideo, setPlayVideo] = useState(false);
    const videoRef = useRef();
    const [participatingEmployers] = useState(
        event?.sponsors?.filter((s) => !!s.companyUrl)
    );
    const [sponsors] = useState(
        event?.sponsors?.filter((s) => !!s.name).length &&
            event?.sponsors?.filter((s) => !!s.name).length < 4
            ? event?.sponsors?.filter((s) => !!s.name)
            : [
                  {
                      name: 'Fairygodboss',
                      image: {
                          src: '/events/d7qtl6hv8fic9bb7icwn',
                          alt: '"Fairygodboss"',
                      },
                  },
              ]
    );

    const {
        startTime,
        endTime,
        eventType,
        registrationDeadline,
        video,
        joinUrl,
    } = event;

    const startTimeObject = countDown(startTime);
    let days;
    let hours;
    let minutes;
    if (startTimeObject) {
        days = startTimeObject.days;
        hours = startTimeObject.hours;
        minutes = startTimeObject.minutes;
    }
    if (eventType === 'Virtual Recruiting Event' && !userRegistered) {
        const registrationgTimeObject = countDown(registrationDeadline);
        if (registrationgTimeObject) {
            days = registrationgTimeObject.days;
            hours = registrationgTimeObject.hours;
            minutes = registrationgTimeObject.minutes;
        }
    }
    const countdownObject = { days, hours, minutes };

    const endTimeCountDown = countDown(endTime);

    let eventStarted = !startTimeObject && endTimeCountDown;
    let earlyStart = eventStarted;

    if (startTimeObject && !days && !hours && !minutes && endTimeCountDown) {
        eventStarted = true;
    }
    if (
        startTimeObject &&
        !days &&
        !hours &&
        endTimeCountDown &&
        (eventType !== 'Virtual Recruiting Event' || userRegistered)
    ) {
        earlyStart = true;
    }
    const eventEnded = !endTimeCountDown;
    const registrationExpired = isInThePast({ date: registrationDeadline });

    const [bannerText, setBannerText] = useState(
        getBannerText({
            userRegistered,
            eventEnded,
            eventType,
            registrationExpired,
            video,
            earlyStart,
            eventStarted,
        })
    );
    const [registrationButtonText, setRegistrationButtonText] = useState(
        getRegistrationText({
            userRegistered,
            eventStarted,
            eventEnded,
            eventType,
            registrationExpired,
            earlyStart,
        })
    );
    const pageContext = useContext(PageContext);

    const handleEventRegisterClickMetrics = useCallback(() => {
        const sponsorIds = event.sponsors.map((company) => company.companyId);
        return EmitMetric({
            misc_event_count: 1,
            misc_event_type: 'event-register-click',
            event_id: event.id,
            company_id: sponsorIds,
        });
    }, [event]);

    const onRegistration = useCallback(async () => {
        const {
            toggleJobProfileSnippetComplete,
            triggerFlowModal,
            jobProfileSnippet,
            user,
            pageProps: { session },
        } = pageContext;

        handleEventRegisterClickMetrics();
        if (!user) {
            return;
        }

        const { vreProfile = {}, vreAdditionalInfo, hash, sourceType } = event;
        const { isCompleted } = jobProfileSnippet;
        const quickProfileRequired = !isCompleted;
        const eventReqInputStepRequired = checkHasRequirements(
            vreAdditionalInfo
        );

        const baseQuickProfile = session.name
            ? { name: session.name, education: {}, experience: {} }
            : {};

        const requiredSteps = {
            [QUICK_PROFILE_STEP_NAME]: quickProfileRequired,
            [EVENT_REQUIREMENTS_INPUT_STEP_NAME]: eventReqInputStepRequired,
        };

        const rawPopulateProps = {
            quickProfile: baseQuickProfile,
            eventRequirementsInput: vreProfile,
        };

        const modalData = {
            stepPopulateProps: registrationFlow.buildDefaultRegFlowState(
                rawPopulateProps
            ),
            generalModalProps: {
                event,
                setUserAsRegistered: () => {
                    setRegistrationButtonText(null);
                    setUserRegistered(true);
                },
                requiredSteps,
                eventPreferredTimes: [],
                startStep: QUICK_PROFILE_STEP_NAME,
                toggleJobProfileSnippetComplete,
                sourceType,
            },
        };

        if (quickProfileRequired) {
            return triggerFlowModal(eventRegistrationFlowType, modalData);
        }

        // no quick profile needed, no event requirements -> register user and check for eventPreferredTimes
        // if we have them, set initial step to time select step otherwise confirmSuccess step
        if (!quickProfileRequired && eventReqInputStepRequired) {
            modalData.generalModalProps.startStep = EVENT_REQUIREMENTS_INPUT_STEP_NAME;
            return triggerFlowModal(eventRegistrationFlowType, modalData);
        }

        if (!quickProfileRequired && !eventReqInputStepRequired) {
            const regPayload = {
                eventHash: hash,
                registerPayload: {},
            };

            const {
                ok,
                data,
            } = await registrationFlowAPI.submitEventRegistration(regPayload);
            if (!ok) {
                return;
            }

            if (!data.eventPreferredTimes) {
                modalData.generalModalProps.startStep = CONFIRM_SUCCESS_STEP_NAME;
                setUserRegistered(true);
                return triggerFlowModal(eventRegistrationFlowType, modalData);
            }

            modalData.generalModalProps.eventPreferredTimes =
                data.eventPreferredTimes;
            modalData.generalModalProps.startStep = SELECT_TIME_STEP_NAME;
            modalData.generalModalProps.requiredSteps[
                SELECT_TIME_STEP_NAME
            ] = true;

            // event has preferred times - set step to time select and trigger modal
            return triggerFlowModal(eventRegistrationFlowType, modalData);
        }
    }, [
        pageContext,
        event,
        setUserRegistered,
        handleEventRegisterClickMetrics,
    ]);

    const onRegistrationButtonClick = useCallback(() => {
        if (bannerText === EVENT_IN_PROGRESS_BANNER && userRegistered) {
            window.open(joinUrl, '_blank');
            return;
        }
        onRegistration();
    }, [bannerText, userRegistered, joinUrl, onRegistration]);

    useEffect(() => {
        const { user, jobProfileSnippet } = pageContext;
        const isInRegistration = Cookies.get('REGISTER_EVENT');
        const { isCompleted } = jobProfileSnippet;

        if (user && isCompleted && isInRegistration && !userRegistered) {
            Cookies.remove('REGISTER_EVENT');
            onRegistration();
        }
    }, [onRegistration, pageContext]);

    return (
        <EventRegistrationContext.Provider
            value={{
                onRegistration,
                setRegistrationButtonText,
                event,
                userRegistered,
                handleEventRegisterClickMetrics,
                registrationButtonText,
                eventStarted: earlyStart || eventStarted,
                eventEnded,
                registrationExpired,
                countdownObject,
                attendeeCount,
                bannerText,
                setBannerText,
                onRegistrationButtonClick,
                videoRef,
                playVideo,
                setPlayVideo,
                sponsors,
                participatingEmployers,
                upcomingEvents,
            }}
        >
            {children}
        </EventRegistrationContext.Provider>
    );
};

const useEventRegistration = () => {
    const context = useContext(EventRegistrationContext);
    if (context === undefined) {
        throw new Error(
            'useEventRegistration can only be used inside EventRegistrationProvider'
        );
    }
    return context;
};

export { EventRegistrationProvider, useEventRegistration };
